import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, OnInit } from '@angular/core';
import { IGenomeTime } from 'bz-genome-angular/time-picker/time-picker/bzg-time-picker.component';
import { GenomeTimeService } from 'app/shared/services/genome-time.service';
import { EJobType } from '../job-type.enum';
import { BzgModalService, BzgToastService, BzgToastType } from 'bz-genome-angular';
import { DeleteConfirmationComponent } from 'app/shared/components/delete-confirmation/delete-confirmation.component';
import { UserJobsService } from 'app/scheduler/user-Jobs/user-jobs.service';
import { TranslateService } from '@ngx-translate/core';
import { I18nService } from 'i18n/i18n.service';
import { IJob } from '../IJob';
import { DateUtil } from 'app/core/utils/DateUtil';
import { IUserJob } from 'app/scheduler/core/IUserJob';
import { ShowToastService } from 'app/shared/services/showToast.service';

@Component({
  selector: 'job-settings',
  templateUrl: 'job-settings.component.html',
  styleUrls: ['job-settings.component.scss']
})
export class JobSettingsComponent implements OnChanges, OnInit {

  @Input() applyDisabled = false;
  @Input() userJobs?: Array<IUserJob>
  @Input() currentJob: IJob;
  @Input() jobType: EJobType;
  @Input() jobStates: { [key: string]: { rescheduleJob: boolean, switchActivated: boolean, originalNextRuntimeDate: Date, nextRuntimeDate: Date } } = {};
  @Input() isDevelopment: boolean;
  @Input() areOtherTabsValid: boolean = false;
  @Output() saveJob = new EventEmitter<IJob>();
  @Output() cancel = new EventEmitter();
  @Output() reset = new EventEmitter();
  @Output() validationChange = new EventEmitter<boolean>();

  public showReset: boolean = false;
  public nextRuntimeDate: Date;
  public nextRuntimeDateTime: any;
  public isValidCurrentJobName: boolean = true;
  public validationMessage: string = '';
  public showValidationMessage: boolean = false;

  @ViewChild('dpTo', { static: false }) dpTo: any;

  constructor(private _modalService: BzgModalService,
    private _userJobService: UserJobsService,
    private _toastService: BzgToastService,
    private _i18nService: I18nService,
    private _showToastService: ShowToastService) {
    this.nextRuntimeDateTime = {
      hours: 12,
      minutes: 0,
      seconds: 0,
      period: 'AM'
    };
  }

  ngOnInit(): void {
    if (!this.nextRuntimeDateTime) {
      this.nextRuntimeDateTime = {
        hours: 12,
        minutes: 0,
        seconds: 0,
        period: 'AM'
      };
    }
    if (this.currentJob) {
      this.updateJobNameValidity(this.currentJob.name);
    }
  }

  ngOnChanges() {
    // Usar el jobStates pasado como Input en lugar del local
    if (!this.jobStates[this.currentJob.guid]) {
      this.jobStates[this.currentJob.guid] = {
        rescheduleJob: false,
        switchActivated: false,
        originalNextRuntimeDate: new Date(this.currentJob.nextRuntime),
        nextRuntimeDate: DateUtil.convertFromLocalToUTCReturnDate(this.currentJob.nextRuntime)
      };
    }
    
    // Asegurar que las referencias sean nuevas instancias
    this.nextRuntimeDate = new Date(this.jobStates[this.currentJob.guid].nextRuntimeDate);
    this.nextRuntimeDateTime = { ...DateUtil.getTimeFromDate(this.nextRuntimeDate) };
    
    if (this.currentJob.disableEditJob && this.dpTo && this.dpTo.onShown) {
      this.dpTo.onShown.isStopped = true;
    }
    if (this.currentJob) {
      this.getStatusReset();
      this.updateJobNameValidity(this.currentJob.name);
      this.updateValidationMessage();
    }
  }

  public validateInputJobName(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.updateJobNameValidity(inputElement.value);
  }

  public setJobNow() {
    this.currentJob.enabled = true;
    this.nextRuntimeDate = DateUtil.convertFromLocalToUTCReturnDate(new Date());
    this.jobStates[this.currentJob.guid].nextRuntimeDate = new Date(this.nextRuntimeDate);
    this.nextRuntimeDateTime = DateUtil.getTimeFromDate(this.nextRuntimeDate);
  }

  public setJobTo9999() {
    this.nextRuntimeDate = new Date(Date.UTC(9999, 11, 31, 23, 59, 59));
    this.jobStates[this.currentJob.guid].nextRuntimeDate = new Date(this.nextRuntimeDate);
    this.nextRuntimeDateTime = DateUtil.getTimeFromDate(this.nextRuntimeDate);
  }

  public applyChanges() {
    DateUtil.setTimeToDate(this.nextRuntimeDate, this.nextRuntimeDateTime);
    this.currentJob.nextRuntime = DateUtil.returnDateToSave(this.nextRuntimeDate);
    this.jobStates[this.currentJob.guid].nextRuntimeDate = this.nextRuntimeDate;
    if (this.currentJob.schedules) {
      this.currentJob.schedules.forEach(schedule => {
        schedule.nextRunTime = this.currentJob.nextRuntime;
      });
    }
    this.saveJob.emit(this.currentJob);
    this.getStatusReset();
  }

  public cancelChanges() {
    DateUtil.setTimeToDate(this.nextRuntimeDate, this.nextRuntimeDateTime);
    this.currentJob.nextRuntime = this.nextRuntimeDate;
    this.jobStates[this.currentJob.guid].nextRuntimeDate = this.nextRuntimeDate;
    this.cancel.emit();
  }

  public isUserJob() {
    return this.jobType === EJobType.User;
  }

  public resetJob() {
    const title = `${this._i18nService.instant('GLOBAL.RESET')} - ${this.currentJob.name}`
    const modal = this._modalService.open(DeleteConfirmationComponent, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      data: { title, message: 'GLOBAL.CONFIRM_RESET', button: 'GLOBAL.RESET' }
    });
    modal.afterClosed().subscribe((result) => {
      if (result === 'delete') {
        this._userJobService.resetStatus(this.currentJob.guid).subscribe((response) => {
          if (response) {
            this.reset.emit();
            this.getStatusReset();
            this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
          }
        });
      }
    });
  }

  public getStatusReset() {
    if (this.currentJob.guid) {
      this._userJobService.getStatusReset(this.currentJob.guid).subscribe((response) => {
        this.showReset = response;
      });
    }
  }

  public checkAndSetDate() {
    const jobState = this.jobStates[this.currentJob.guid];
    if (jobState.rescheduleJob) {
      this.setJobNow();
      jobState.switchActivated = true;
    } else {
      this.setJobTo9999();
      jobState.switchActivated = false;
    }
    // Asegurar que nextRuntimeDate esté sincronizado
    jobState.nextRuntimeDate = new Date(this.nextRuntimeDate);
  }

  public isNextRuntimeYear9999(): boolean {
    return this.jobStates[this.currentJob.guid].originalNextRuntimeDate.getFullYear() === 9999;
  }

  public shouldShowDateSection(): boolean {
    const jobState = this.jobStates[this.currentJob.guid];
    return !this.isNextRuntimeYear9999() || jobState.rescheduleJob;
  }

  public shouldShowSwitch(): boolean {
    const jobState = this.jobStates[this.currentJob.guid];
    return this.isNextRuntimeYear9999() || jobState.switchActivated;
  }

  public shouldDisableButton(): boolean {
    const jobState = this.jobStates[this.currentJob.guid];
    return this.shouldShowSwitch() && !jobState.rescheduleJob;
  }

  public onDateChange(newDate: Date) {
    if (newDate && this.currentJob?.guid) {
      this.nextRuntimeDate = newDate;
      this.jobStates[this.currentJob.guid].nextRuntimeDate = new Date(newDate);
    }
  }

  // Verificar comportamiento con geneme
  public onTimeChange(newTime: any) {
    console.log('onTimeChange triggered with:', newTime);
    console.log('Current nextRuntimeDateTime:', this.nextRuntimeDateTime);
    
    if (newTime && this.currentJob?.guid) {
      this.nextRuntimeDateTime = { ...newTime };
      
      // Crear una nueva instancia de la fecha para evitar mutación
      const updatedDate = new Date(this.nextRuntimeDate);
      console.log('Date before setTimeToDate:', updatedDate);
      
      DateUtil.setTimeToDate(updatedDate, this.nextRuntimeDateTime);
      console.log('Date after setTimeToDate:', updatedDate);
      
      // Actualizar ambas referencias
      this.nextRuntimeDate = updatedDate;
      this.jobStates[this.currentJob.guid].nextRuntimeDate = new Date(updatedDate);
      
      console.log('Final nextRuntimeDate:', this.nextRuntimeDate);
      console.log('Final jobState date:', this.jobStates[this.currentJob.guid].nextRuntimeDate);
    }
  }

  private updateValidationMessage(): void {
    if (this.isDevelopment) {
      this.resetValidationMessage();
      return;
    }

    const hasInvalidJobName = !this.isValidCurrentJobName;
    const hasOtherValidationIssues = !this.areOtherTabsValid;

    this.showValidationMessage = hasInvalidJobName || hasOtherValidationIssues;
    this.validationMessage = this.getValidationMessageKey(hasInvalidJobName, hasOtherValidationIssues);
  }

  private resetValidationMessage(): void {
    this.showValidationMessage = false;
    this.validationMessage = '';
  }

  private getValidationMessageKey(hasInvalidJobName: boolean, hasOtherValidationIssues: boolean): string {
    if (!hasInvalidJobName && !hasOtherValidationIssues) {
      return '';
    }
    
    if (hasInvalidJobName && hasOtherValidationIssues) {
      return 'JOBS.NAME_AND_OTHER_TABS_VALIDATION_MESSAGE';
    }
    
    return hasInvalidJobName 
      ? 'JOBS.NAME_VALIDATION_MESSAGE'
      : 'JOBS.USERJOBS_VALIDATION_ERRORS_IN_OTHER_TABS';
  }

  private updateJobNameValidity(value: string): void {
    this.isValidCurrentJobName = this.validateJobName(value);
    this.updateValidationMessage();
    this.validationChange.emit(this.isValidCurrentJobName);
  }

  private validateJobName(jobName: string): boolean {
    const isValidFormat = /^[a-zA-Z0-9]*$/.test(jobName);
    const isValidLength = jobName.length >= 1 && jobName.length <= 24;

    if (!isValidFormat || !isValidLength) {
      return false;
    }

    if (this.userJobs && this.currentJob) {
      const nameExists = this.userJobs.some(
        job =>
          job.name.toLowerCase() === jobName.toLowerCase() &&
          job.guid !== this.currentJob.guid
      );
      if (nameExists) {
        return false;
      }
    }

    return true;
  }
}